import type { UserType } from 'tools/constants/users';

import { UserActivationActionType } from 'components/userActivationControl';

import { AndOrOperation, type SortOrder } from 'tools/types';

import { BASE_URL } from '.';

import type { UsersSortByKey } from 'tools/types/users';

const API_VERSION = 'v3';

export const USERS_BASE_URL = `${BASE_URL}/${API_VERSION}/users`;
export const USER_SCHOOLS_URL = `${USERS_BASE_URL}/schools`;

interface UserProps {
  userID: string | number;
}

export const getUserUrl = ({ userID }: UserProps) =>
  `${USERS_BASE_URL}/${userID}`;

export const getUserCourseUrl = (props: UserProps) =>
  `${getUserUrl(props)}/courses`;

export const getUserCourseExportUrl = ({
  type,
  ...props
}: UserProps & { type: 'fullRecord' | 'summary' }) =>
  `${getUserCourseUrl(props)}/export/${type}`;

export const getUserOverviewUrl = (userID: number | string) =>
  `${getUserUrl({ userID })}/overview`;

export interface GetUsersFilters {
  activationStatuses?: UserActivationActionType[];
  excludeUsersFromGroupId?: number;
  isGroupAndRelationship?: boolean;
  limit?: number;
  page?: number;
  schoolIDs?: string[];
  search?: string;
  sortBy?: UsersSortByKey;
  sortOrder?: SortOrder;
  userGroupIDs?: number[];
  userType?: UserType | '';
}

export const getUsersUrl = ({
  filters,
  onlyParams = false,
  shouldPaginate = true,
}: {
  filters: GetUsersFilters;
  onlyParams?: boolean;
  shouldPaginate?: boolean;
}) => {
  const params = new URLSearchParams();
  if (filters?.schoolIDs && filters?.schoolIDs.length > 0) {
    params.append('schoolIDs', filters.schoolIDs.join(','));
  }

  if (filters?.search) {
    params.append('name', filters.search);
  }

  if (filters?.userGroupIDs && filters?.userGroupIDs.length > 0) {
    params.append('userGroupIDs', filters.userGroupIDs.join(','));
    params.append(
      'userGroupOperation',
      filters.isGroupAndRelationship ? AndOrOperation.AND : AndOrOperation.OR,
    );
  }

  if (filters?.userType) {
    params.append('userType', filters.userType);
  }

  if (filters?.activationStatuses?.length) {
    const { activationStatuses } = filters;

    const hasActivate = activationStatuses.includes(
      UserActivationActionType.Activate,
    );
    const hasDeactivate = activationStatuses.includes(
      UserActivationActionType.Deactivate,
    );

    if (hasActivate !== hasDeactivate) {
      params.append('isActive', hasActivate ? 'true' : 'false');
    }
  }
  if (shouldPaginate) {
    if (filters?.page !== undefined) {
      params.append('page', filters.page.toString());
    }

    if (filters?.limit !== undefined) {
      params.append('limit', filters.limit.toString());
    }
  }

  if (filters?.sortBy && filters?.sortOrder) {
    params.append('sortBy', filters.sortBy);
    params.append('sortOrder', filters.sortOrder);
  }

  if (filters?.excludeUsersFromGroupId) {
    params.append(
      'excludeUsersFromGroupId',
      filters.excludeUsersFromGroupId.toString(),
    );
  }

  const queryString = params.toString();

  if (onlyParams) {
    return queryString;
  }
  return queryString ? `${USERS_BASE_URL}?${queryString}` : USERS_BASE_URL;
};
